/**
 * Theme manager — light is the default, even when the OS is in dark mode.
 *
 * The user picks one of:
 *   - 'light'  (default for new installs)
 *   - 'dark'   (force dark regardless of OS)
 *   - 'system' (follow prefers-color-scheme)
 *
 * `applyTheme` writes the data-theme attribute on <html> (CSS variables in
 * index.css read it) and rewrites the theme-color meta so the iOS/Android
 * status bar matches what's painted on screen.
 *
 * `applyAccent` writes a custom accent RGB triplet onto the --accent CSS
 * variable. Each accent key has separate light/dark values so the chosen
 * color stays vivid in either theme.
 */

import { accentPalette, stellarAccent, type AccentKey } from '../tokens';

export type ThemePreference = 'light' | 'dark' | 'system';

export const DEFAULT_THEME: ThemePreference = 'light';

/** Sentinel accent key for the rate-prompt unlock. */
export const STELLAR_ACCENT_KEY = 'stellar';

const THEME_COLOR_LIGHT = '#F5F5F7';
const THEME_COLOR_DARK = '#000000';

const isThemePreference = (v: unknown): v is ThemePreference =>
  v === 'light' || v === 'dark' || v === 'system';

const isAccentKey = (v: unknown): v is AccentKey =>
  typeof v === 'string' && v in accentPalette;

const isDarkResolved = (theme: ThemePreference): boolean => {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const resolveStatusBarColor = (theme: ThemePreference): string =>
  isDarkResolved(theme) ? THEME_COLOR_DARK : THEME_COLOR_LIGHT;

const setMetaThemeColor = (color: string) => {
  if (typeof document === 'undefined') return;
  let meta = document.head.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = color;
};

/**
 * Apply the chosen theme to the document. Safe to call repeatedly — it
 * just rewrites the attribute and meta color.
 */
export function applyTheme(theme: ThemePreference): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  setMetaThemeColor(resolveStatusBarColor(theme));
}

/** Resolve a stored accent key to its RGB triplet pair (light + dark). */
function resolveAccentPair(key: string): { light: string; dark: string } {
  if (key === STELLAR_ACCENT_KEY) return stellarAccent;
  if (isAccentKey(key)) return accentPalette[key];
  return accentPalette.blue;
}

/**
 * Apply the user-picked accent. Writes the right light/dark RGB triplet
 * onto the --accent CSS variable based on the resolved theme.
 */
export function applyAccent(key: string, theme: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const palette = resolveAccentPair(key);
  const dark = isDarkResolved(theme);
  document.documentElement.style.setProperty(
    '--accent',
    dark ? palette.dark : palette.light,
  );
}

/**
 * Watch the OS-level preference and re-apply the meta color + accent when
 * the user is in 'system' mode. Returns an unsubscribe fn.
 */
export function watchSystemTheme(
  getCurrent: () => { theme: ThemePreference; accentColor: string },
): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    const { theme, accentColor } = getCurrent();
    if (theme !== 'system') return;
    setMetaThemeColor(resolveStatusBarColor(theme));
    applyAccent(accentColor, theme);
  };
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}

/** Narrow an arbitrary value to a ThemePreference, falling back to default. */
export function coerceTheme(value: unknown): ThemePreference {
  return isThemePreference(value) ? value : DEFAULT_THEME;
}
